import { getHighAccuracyLocation } from "../services/UserTracking";

/**
 * Detect if the current device is a mobile phone/tablet browser.
 * Checks both user agent and touch capability.
 */
export const isMobileDevice = () => {
  const ua = navigator.userAgent || "";
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i;
  const hasTouchScreen = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  return mobileRegex.test(ua) && hasTouchScreen;
};

/**
 * Get the device type string for backend recording.
 */
export const getDeviceType = () => {
  return isMobileDevice() ? "mobile" : "desktop";
};

/**
 * Get location from the browser's native Geolocation API (uses phone GPS hardware).
 * Returns { lat, lng, accuracy } or throws on failure.
 */
const getBrowserGPSLocation = (timeoutMs = 15000) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error("Geolocation not supported"));
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (err) => {
        reject(err);
      },
      {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 0,
      }
    );
  });
};

/**
 * Get location from the server-side Google Geolocation API (IP-based).
 * Used as fallback for desktop or when GPS is denied.
 * Returns { lat, lng, accuracy } or null.
 */
const getServerIPLocation = async () => {
  const result = await getHighAccuracyLocation();
  if (result?.locations) {
    return {
      lat: result.locations.lat,
      lng: result.locations.lng,
      accuracy: result.locations.accuracy || null,
    };
  }
  return null;
};

/**
 * Smart location resolver:
 * - Mobile → browser GPS (hardware), falls back to server IP
 * - Desktop → server IP geolocation, falls back to browser GPS
 *
 * Returns { lat, lng, accuracy, source: 'gps'|'ip' } or null if both fail.
 */
export const getDeviceLocation = async () => {
  const mobile = isMobileDevice();

  if (mobile) {
    // Mobile: prefer GPS hardware
    try {
      const loc = await getBrowserGPSLocation();
      return { ...loc, source: "gps" };
    } catch (gpsErr) {
      // GPS denied or timed out — try server IP as fallback
      try {
        const loc = await getServerIPLocation();
        return loc ? { ...loc, source: "ip" } : null;
      } catch {
        return null;
      }
    }
  } else {
    // Desktop: prefer server IP geolocation
    try {
      const loc = await getServerIPLocation();
      if (loc) return { ...loc, source: "ip" };
    } catch {
      // Server IP failed — fall through to browser GPS
    }

    // Fallback: try browser GPS (some desktops have it via WiFi)
    try {
      const loc = await getBrowserGPSLocation(10000);
      return { ...loc, source: "gps" };
    } catch {
      return null;
    }
  }
};
